// Pure bucketing for the admin Sign-ups dashboard (SIGNUP_ABUSE_DEFENSE_PLAN.md
// Task 3.2, D-15). Keys come from zonedBucketRange in the browser's zone.

import { zonedBucketKey, type AdminSignupEvent, type SignupOutcome } from "@checklist/shared";

export interface SignupBucketCounts {
  allowed: number;
  confirmed: number;
  blockedTurnstile: number;
  blockedAddress: number;
  blockedGate: number;
}

export interface SignupEventSummary {
  allowed: number;
  confirmed: number;
  blocked: number;
  /** confirmed / allowed. Null when nothing was allowed, so the rate is not 0/0. */
  confirmationRate: number | null;
}

const ALLOWED: ReadonlySet<SignupOutcome> = new Set([
  "allowed",
  "allowed_allowlist",
  "allowed_approved",
]);

const ADDRESS: ReadonlySet<SignupOutcome> = new Set([
  "rejected_address",
  "rejected_suppressed",
  "rejected_duplicate",
]);

const GATE: ReadonlySet<SignupOutcome> = new Set(["rejected_mode", "rejected_breaker"]);

function emptyCounts(): SignupBucketCounts {
  return { allowed: 0, confirmed: 0, blockedTurnstile: 0, blockedAddress: 0, blockedGate: 0 };
}

function tally(counts: SignupBucketCounts, event: AdminSignupEvent): void {
  if (event.kind === "confirmed") {
    counts.confirmed += 1;
    return;
  }
  const outcome = event.outcome;
  if (!outcome) return;
  if (ALLOWED.has(outcome)) counts.allowed += 1;
  else if (outcome === "rejected_turnstile") counts.blockedTurnstile += 1;
  else if (ADDRESS.has(outcome)) counts.blockedAddress += 1;
  else if (GATE.has(outcome)) counts.blockedGate += 1;
}

export function bucketSignupEvents(
  events: AdminSignupEvent[],
  zone: string,
  unit: "day" | "hour",
  keys: string[]
): SignupBucketCounts[] {
  const index = new Map(keys.map((key, i) => [key, i]));
  const buckets = keys.map(() => emptyCounts());
  for (const event of events) {
    if (Number.isNaN(Date.parse(event.at))) continue;
    const slot = index.get(zonedBucketKey(event.at, zone, unit));
    if (slot === undefined) continue;
    tally(buckets[slot]!, event);
  }
  return buckets;
}

export function summarizeSignupEvents(events: AdminSignupEvent[]): SignupEventSummary {
  const totals = emptyCounts();
  for (const event of events) tally(totals, event);
  const blocked = totals.blockedTurnstile + totals.blockedAddress + totals.blockedGate;
  return {
    allowed: totals.allowed,
    confirmed: totals.confirmed,
    blocked,
    confirmationRate: totals.allowed > 0 ? totals.confirmed / totals.allowed : null,
  };
}
